import { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { Transcript } from '../api/models/Transcript';
import { EarningsDate } from '../api/models/EarningsDate';
import { getEarningsDates } from '../api/thunks/financials';
import { getEarningsTranscript } from '../api/thunks/transcripts';
import { setNotification } from '../store/notifications-reducer';
import { useLocation } from 'react-router-dom';
import { setSelectedTranscript } from '../store/layout-reducer';

export const useTranscript = (
  supportedTickers: string[],
  accessToken: string | undefined,
  isShareMode: boolean,
  conversationId: string | undefined
) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const shareLinkToken = new URLSearchParams(location.search).get('share') ?? undefined;
  const conversationLayout = useAppSelector(state => conversationId ? state.layout[conversationId] : undefined);

  const [currentTicker, setCurrentTickerState] = useState(conversationLayout?.selectedTranscript?.ticker || supportedTickers[0] || '');
  const [fiscalDateEnding, setFiscalDateEndingState] = useState(conversationLayout?.selectedTranscript?.fiscalDateEnding || '');

  const [transcript, setTranscript] = useState<Transcript | undefined>(undefined);
  const [dates, setDates] = useState<EarningsDate[]>([]);

  useEffect(() => {
    if (conversationLayout) {
        setCurrentTickerState(conversationLayout.selectedTranscript?.ticker || supportedTickers[0] || '');
        setFiscalDateEndingState(conversationLayout.selectedTranscript?.fiscalDateEnding || '');
    }
  }, [conversationId, conversationLayout]);

  const setCurrentTicker = (ticker: string) => {
    setCurrentTickerState(ticker);
    setFiscalDateEndingState(''); // Reset fiscal date when ticker changes
    if (conversationId) {
      dispatch(setSelectedTranscript({
        conversationId,
        transcript: {
          ticker,
          fiscalDateEnding: ''
        }
      }));
    }
  };

  const setCurrentFiscalDateEnding = (date: string) => {
    setFiscalDateEndingState(date);
    if (conversationId && currentTicker) {
      dispatch(setSelectedTranscript({
        conversationId,
        transcript: {
          ticker: currentTicker,
          fiscalDateEnding: date
        }
      }));
    }
  };

  // Update ticker if supportedTickers changes and current isn't in the list
  useEffect(() => {
    if (supportedTickers.length > 0 && !supportedTickers.includes(currentTicker)) {
      setCurrentTicker(supportedTickers[0]);
    }
  }, [supportedTickers, currentTicker]);

  // Load dates when ticker changes
  useEffect(() => {
    if (!currentTicker || (!accessToken && !isShareMode)) return;

    const loadTranscriptDates = async () => {
      try {
        const data = await getEarningsDates(
          { ticker: currentTicker, entityType: 'transcript' },
          { accessToken, shareLinkToken }
        );

        setDates(data.dates);

        // If we have dates, use the most recent one with access
        if (data.dates.length > 0) {
          if (!fiscalDateEnding) {
            const mostRecentWithAccess = data.dates.find(date => date.userHasAccess);
            if (mostRecentWithAccess) {
              // When ticker changes, always update the fiscal date to match the new ticker's latest date
              setCurrentFiscalDateEnding(mostRecentWithAccess.date);
            }
          }
        } else {
          // Clear the fiscal date if no dates are available
          setCurrentFiscalDateEnding('');
        }
      } catch (error) {
        console.error(error);
        dispatch(setNotification({
          message: 'Failed to get transcript dates',
          type: 'error'
        }));
      }
    };

    loadTranscriptDates();
  }, [currentTicker, accessToken, shareLinkToken]);

  // Load transcript only after we have both ticker and fiscalDateEnding
  useEffect(() => {
    if (!currentTicker || !fiscalDateEnding || (!accessToken && !isShareMode)) return;

    const loadTranscript = async () => {
      try {
        const data = await getEarningsTranscript(
          currentTicker,
          fiscalDateEnding,
          { accessToken, shareLinkToken },
          dispatch
        );

        setTranscript(data);
      } catch (error) {
        dispatch(setNotification({
          message: 'Failed to get transcript',
          type: 'error'
        }));
      }
    };

    loadTranscript();
  }, [fiscalDateEnding, accessToken, isShareMode, shareLinkToken]);

  return {
    currentTicker,
    setCurrentTicker,
    transcript,
    fiscalDateEnding,
    setCurrentFiscalDateEnding,
    dates
  };
};